import moment from "moment";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { useQueryParamsHook } from "@/hooks/useQueryParamsHook";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useDeleteSurveyMutation, useMySurveysQuery, useSurveyQuery } from "@/hooks/ReactQuery/SurveyReactQuery/SurveyReactQuery";
import { STATUS_LABEL, surveyUrl } from "./surveyQuestions";
import { FgSurveyEditor } from "./fragments/FgSurveyEditor";

const ICON_BUTTON = "rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100";

/**
 * 설문 탭: 내 설문 목록 ↔ 설문 만들기·고치기 (?surveyId=new | 설문 ID, 응답 보기는 &surveyView=RESULTS).
 * 설문마다 shool.uk/{페이지}/f/{설문 ID} 주소가 생기고, "내 페이지에 넣기"로 페이지에 링크 버튼을 넣는다.
 */
export function FdSurveys() {
    const userPage = useUserPageContextValueHook()?.userPage;
    const queryParamsHook = useQueryParamsHook();
    const surveyId = queryParamsHook?.params?.surveyId;
    const view = queryParamsHook?.params?.surveyView;

    const surveysQuery = useMySurveysQuery({ userPageId: userPage?.id });
    const surveyQuery = useSurveyQuery({ id: surveyId && surveyId !== 'new' ? surveyId : null });
    const deleteSurveyMutation = useDeleteSurveyMutation();
    const surveys = surveysQuery.data || [];

    const setParams = (patch, replace) => {
        const params = { ...queryParamsHook?.params, ...patch };
        Object.keys(params).forEach(key => params[key] == null && delete params[key]);
        queryParamsHook.setSearchParams(params, { replace });
    }
    const openSurvey = (id, nextView = null) => setParams({ surveyId: id, surveyView: nextView }, false);

    const handleDelete = (survey) => {
        if (!window.confirm(`"${survey.title}" 설문을 삭제할까요? 받은 응답 ${survey.responseCount}개도 함께 지워지고 되돌릴 수 없어요.`)) {
            return;
        }
        deleteSurveyMutation.mutate({ id: survey.id }, { onSuccess: () => customToast.success('설문을 삭제했어요.') });
    }

    if (!userPage?.id) {
        return null;
    }

    if (surveyId) {
        if (surveyId !== 'new' && surveyQuery.isLoading) {
            return <div className="h-96 animate-pulse rounded-[15px] bg-white" aria-busy="true" />;
        }
        if (surveyId !== 'new' && !surveyQuery.data) {
            return (
                <div className="rounded-[15px] bg-white p-8 text-center">
                    <p className="text-sm font-semibold">설문을 찾을 수 없어요.</p>
                    <button type="button" onClick={() => setParams({ surveyId: null, surveyView: null }, true)} className="mt-3 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white">설문 목록으로</button>
                </div>
            );
        }
        return (
            <FgSurveyEditor
                key={surveyId === 'new' ? 'new' : surveyQuery.data.id}
                userPageId={userPage.id}
                domainName={userPage.domainName}
                survey={surveyId === 'new' ? null : surveyQuery.data}
                view={view}
                onChangeView={(next) => setParams({ surveyView: next }, true)}
                onBack={() => setParams({ surveyId: null, surveyView: null }, true)}
                onSaved={(saved) => (surveyId === 'new' ? setParams({ surveyId: saved.id }, true) : surveyQuery.refetch())}
            />
        );
    }

    return (
        <section className="flex flex-col gap-4 rounded-[15px] border border-gray-100 bg-white p-5 shadow-[var(--defaultBoxShadow)] min-[993px]:p-7">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-xl font-bold">설문 <span className="text-base font-semibold text-[var(--mainColor)]">{surveys.length}</span></h2>
                    <p className="mt-1 text-sm text-gray-500 break-keep">신청서·수요조사·만족도 조사를 만들고 응답을 모아 보세요. 설문마다 <b className="text-gray-700">shool.uk/{userPage.domainName}/f/...</b> 주소가 생겨요.</p>
                </div>
                <button type="button" onClick={() => openSurvey('new')} className="shrink-0 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-gray-700">
                    ＋ 새 설문
                </button>
            </div>

            {surveysQuery.isLoading &&
                <ul className="grid gap-2.5 min-[600px]:grid-cols-2" aria-busy="true">
                    {[0, 1].map(i => <li key={i} className="h-32 animate-pulse rounded-2xl bg-gray-100" />)}
                </ul>
            }

            {!surveysQuery.isLoading && surveys.length === 0 &&
                <button type="button" onClick={() => openSurvey('new')} className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 px-6 py-12 text-center transition hover:border-gray-300 hover:bg-gray-50">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#ee9ca7] to-[#8b7cf6] text-xl text-white shadow-md shadow-[#8b7cf6]/20" aria-hidden="true">✓</span>
                    <span className="mt-1 text-[15px] font-bold text-gray-900">첫 설문을 만들어 보세요</span>
                    <span className="text-[13px] text-gray-500">객관식·체크박스·별점·그리드 등 10가지 질문과 섹션 나누기를 쓸 수 있어요.</span>
                </button>
            }

            {surveys.length > 0 &&
                <ul className="grid gap-2.5 min-[600px]:grid-cols-2">
                    {surveys.map(survey => (
                        <li key={survey.id} className="group flex flex-col rounded-2xl bg-white p-4 ring-1 ring-gray-200 transition hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:ring-gray-300">
                            <button type="button" onClick={() => openSurvey(survey.id)} className="flex flex-1 flex-col items-start text-left">
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_LABEL[survey.status].className}`}>
                                    {survey.status === 'OPEN' && <span className="mr-1 inline-block h-1.5 w-1.5 -translate-y-px rounded-full bg-emerald-500" aria-hidden="true" />}
                                    {STATUS_LABEL[survey.status].label}
                                </span>
                                <span className="mt-2 line-clamp-2 break-words text-[16px] font-bold text-gray-900">{survey.title}</span>
                                <span className="mt-1 text-[12px] text-gray-400">
                                    질문 {survey.questionCount}개 · {moment(survey.updatedAt).format('MM.DD HH:mm')} 수정
                                    {survey.status === 'OPEN' && survey.closeAt && ` · ${moment(survey.closeAt).format('MM.DD HH:mm')} 마감`}
                                    {survey.status === 'OPEN' && survey.maxResponses && ` · ${survey.maxResponses.toLocaleString()}개까지`}
                                </span>
                            </button>
                            <div className="mt-3 flex items-center gap-1 border-t border-gray-100 pt-3">
                                <button type="button" onClick={() => openSurvey(survey.id, 'RESULTS')} className="mr-auto rounded-lg px-1 py-1 text-left">
                                    <span className="text-[20px] font-extrabold tabular-nums text-gray-900">{survey.responseCount.toLocaleString()}</span>
                                    <span className="ml-1 text-[12px] font-semibold text-gray-500">응답 보기 →</span>
                                </button>
                                {survey.status !== 'DRAFT' &&
                                    <a href={surveyUrl(userPage.domainName, survey.id)} target="_blank" rel="noreferrer" className={ICON_BUTTON}>열기</a>
                                }
                                <button type="button" onClick={() => handleDelete(survey)} className={`${ICON_BUTTON} hover:text-[var(--redColor1)]`}>삭제</button>
                            </div>
                        </li>
                    ))}
                </ul>
            }
        </section>
    );
}
